import { useState } from 'react';
import type { SubmitEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { CoffeeViewNavState } from '@/analytics/events';
import { trackCoffeeCheckedIn } from '@/analytics/events';
import { BrewSelector } from '@/components/common/BrewSelector';
import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ScorePicker } from '@/components/common/ScorePicker';
import { Layout } from '@/components/Layout';
import { useCoffee } from '@/hooks/useCoffee';
import { useCreateRating } from '@/hooks/useCreateRating';
import type { BrewType } from '@/lib/brewTypes';
import { getRandomTastingNotePlaceholder } from '@/lib/tastingNotePlaceholders';

const MAX_NOTES_LENGTH = 255;

export function CoffeeCheckInPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { coffee, isLoading, error: coffeeError, refetch } = useCoffee(id);
  const { createRating, isSubmitting, error: submitError } = useCreateRating();

  const [score, setScore] = useState<number | null>(null);
  const [brewType, setBrewType] = useState<BrewType | null>(null);
  const [tastingNotes, setTastingNotes] = useState('');
  const [placeholder] = useState(getRandomTastingNotePlaceholder);

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!coffee || score === null) return;
    const trimmed = tastingNotes.trim();

    const result = await createRating({
      coffeeId: coffee.id,
      tastingNotes: trimmed.length === 0 ? null : trimmed,
      brewType,
      score,
    });

    if (result) {
      // Client intent/UX signal. The authoritative `rating_created` is
      // emitted server-side (#253) — don't emit it here too.
      trackCoffeeCheckedIn({
        coffee_id: coffee.id,
        score,
        has_notes: trimmed.length > 0,
        brew_type: brewType,
      });
      void navigate('/', { replace: true });
    }
  };

  const canSubmit = coffee !== null && score !== null && !isSubmitting;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {isLoading && (
          <LoadingSpinner label="Loading coffee" testId="coffee-loading" />
        )}

        {!isLoading && coffeeError && (
          <ErrorState
            message="Failed to load coffee. Please try again."
            onPrimaryAction={() => void refetch()}
            testId="coffee-check-in-error"
          />
        )}

        {!isLoading && !coffeeError && !coffee && (
          <div
            className="flex justify-center items-center py-12"
            data-testid="coffee-not-found"
          >
            <p className="text-gray-500 text-center">Coffee not found</p>
          </div>
        )}

        {!isLoading && !coffeeError && coffee && (
          <article className="bg-wolisho-card rounded-xl p-4 md:p-6" data-testid="check-in-page">
            <header className="mb-6">
              <p className="text-wolisho-brown text-sm font-semibold uppercase tracking-wide">
                Check in
              </p>
              <h1 className="text-black text-3xl font-bold mt-1">{coffee.name}</h1>
              {coffee.roaster && (
                <p className="text-black text-sm font-semibold mt-1">
                  by <span className="text-wolisho-red">{coffee.roaster.name}</span>
                </p>
              )}
            </header>

            <form
              onSubmit={(event) => void handleSubmit(event)}
              data-testid="check-in-form"
              className="flex flex-col gap-6"
            >
              <div className="flex flex-col items-center gap-2">
                <span className="font-semibold text-wolisho-brown">Score</span>
                <ScorePicker
                  value={score}
                  onChange={setScore}
                  testIdPrefix="check-in-score"
                />
              </div>

              <div className="flex flex-col items-center gap-2">
                <span className="font-semibold text-wolisho-brown">
                  Brew method (optional)
                </span>
                <BrewSelector value={brewType} onChange={setBrewType} />
              </div>

              <label className="flex flex-col gap-2">
                <span className="font-semibold text-wolisho-brown text-center">
                  Tasting notes (optional)
                </span>
                <textarea
                  data-testid="check-in-notes"
                  value={tastingNotes}
                  onChange={(event) => setTastingNotes(event.target.value)}
                  maxLength={MAX_NOTES_LENGTH}
                  rows={4}
                  placeholder={placeholder}
                  className="rounded-lg border border-wolisho-brown/20 bg-wolisho-cream p-3 text-black focus:outline-hidden focus:border-wolisho-red"
                />
                <span
                  className="text-xs text-wolisho-brown/70 self-end"
                  data-testid="check-in-notes-count"
                >
                  {tastingNotes.length}/{MAX_NOTES_LENGTH}
                </span>
              </label>

              {submitError && (
                <p
                  role="alert"
                  data-testid="check-in-error"
                  className="text-wolisho-red text-sm"
                >
                  {submitError.message}
                </p>
              )}

              <div className="flex flex-col-reverse md:flex-row md:justify-center gap-3">
                <Link
                  to={`/coffee/${coffee.id}`}
                  state={{ source: 'check_in' } satisfies CoffeeViewNavState}
                  data-testid="check-in-cancel"
                  className="px-6 py-3 rounded-lg font-semibold text-wolisho-brown border border-wolisho-brown/30 text-center hover:border-wolisho-red transition-colors"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="check-in-submit"
                  className="px-6 py-3 bg-wolisho-red text-white font-semibold rounded-lg disabled:opacity-60 disabled:cursor-not-allowed hover:bg-wolisho-red/90 transition-colors"
                >
                  {isSubmitting ? 'Submitting…' : 'Submit check-in'}
                </button>
              </div>
            </form>
          </article>
        )}
      </div>
    </Layout>
  );
}
